import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from 'src/app/services/dialog.service';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import Swal from 'sweetalert2';
import { TemplateCategoriesService } from './template-categories.service';
import { AddTemplateCategoryComponent } from './add-template-category/add-template-category.component';
import { EditTemplateCategoryComponent } from './edit-template-category/edit-template-category.component';
import { TemplateOrganizationBrowserComponent } from './template-organization-browser/template-organization-browser.component';
import { helper } from 'src/environments/helper';
import { PermissionsService } from '../../../services/permissions.service';
import { FormControl, FormGroup } from '@angular/forms';
import { CommonService } from 'src/app/services/common.service';
import { Generic } from 'src/app/interfaces/generic.interface';

export interface TemplateCategory {
    id: number;
    name: string;
    description?: string;
    directie_id: number;
    compartiment_id?: number;
    parent_category_id?: number;
    active: boolean;
    public_visible: boolean;
    order_index: number;
    created_at?: string;
    directie_info?: any;
    compartiment_info?: any;
    template_counts?: {
        document_templates: number;
        request_templates: number;
        total_assignments: number;
    };
}

@Component({
    selector: 'app-template-categories',
    templateUrl: './template-categories.component.html',
    styleUrls: ['./template-categories.component.scss'],
})
export class TemplateCategoriesComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;

    helper = helper;
    Object = Object;
    isLoading = false;
    isTreeLoading = false;
    isFilterLoading = false;
    showCard = false;
    showTreeView = false;

    activeValue: string = '-1';
    publicValue: string = '-1';

    // Tree view data
    categoryTree: any[] = [];
    statistics: any = {};

    directii: any[] = [];
    compartimente!: Generic[];

    private destroy$ = new Subject<void>();

    // Filtered fields for search
    filteredDirectii!: any[];
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    // DataTable configuration
    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;
    dtLink = this.templateCategoriesService.getListDTUrl();

    // Filter parameters
    parameters: { key: string; value: string }[] = [
        { key: '--Alegeți--', value: '-1' },
        { key: 'Da', value: '1' },
        { key: 'Nu', value: '0' },
    ];

    searchParamsForm!: FormGroup;

    //Form Initial Values

    initialFormValues = {
        directie_id: -1,
        compartiment_id: -1,
        name: '',
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            directie_id: new FormControl(-1),
            compartiment_id: new FormControl(-1),
            name: new FormControl(''),
        });
    }

    constructor(
        private dialogService: DialogService,
        private http: HttpClient,
        private dialog: MatDialog,
        private templateCategoriesService: TemplateCategoriesService,
        public permissionsService: PermissionsService,
        private commonService: CommonService,
    ) { }

    ngOnInit(): void {
        this.loadDirectiiList();
        this.loadStatistics();
        this.renderDT();
        this.buildSearchParamsForm();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directii;
                    return of(this.directii);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimente;
                    return of(this.compartimente);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directii.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    loadDirectiiList(): void {
        this.isFilterLoading = true;
        this.commonService
            .getDirectiiActive()
            .pipe(
                map((results: any) => [...results]),
                finalize(() => this.isFilterLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.directii = results;

                    this.filteredDirectii = [...this.directii];
                },
                error: (error) => {
                    console.error('Error loading directii', error);
                    this.filteredDirectii = [];
                },
            });
    }

    loadStatistics(): void {
        this.templateCategoriesService.getStatistics().subscribe({
            next: (response) => {
                if (response.errors === false) {
                    this.statistics = response.data;
                }
            },
            error: (error) => {
                console.error('Error loading statistics:', error);
            }
        });
    }

    loadTreeView(): void {
        this.isTreeLoading = true;
        this.templateCategoriesService.getTreeStructure(this.searchParamsForm.get('directie_id')?.value || undefined, this.searchParamsForm.get('compartiment_id')?.value || undefined)
            .subscribe({
                next: (response) => {
                    this.isTreeLoading = false;
                    if (response.errors === false) {
                        this.categoryTree = response.data;
                    }
                },
                error: (error) => {
                    this.isTreeLoading = false;
                    console.error('Error loading tree view:', error);
                }
            });
    }

    toggleTreeView(): void {
        this.showTreeView = !this.showTreeView;
        if (this.showTreeView) {
            this.loadTreeView();
        }
    }

    openOrganizationBrowser(): void {
        const dialogRef = this.dialog.open(TemplateOrganizationBrowserComponent, {
            height: '90vh',
            width: '95vw',
            maxWidth: '1400px',
            data: {
                directie_id: this.searchParamsForm.get('directie_id')?.value,
                compartiment_id: this.searchParamsForm.get('compartiment_id')?.value
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.refresh) {
                this.rerenderDT();
                this.loadStatistics();
                if (this.showTreeView) {
                    this.loadTreeView();
                }
            }
        });
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(categorie =>
                categorie.id === this.searchParamsForm.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directii;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.searchParamsForm.controls['compartiment_id'].patchValue(-1);

        if (directieId == -1) {
            this.compartimente = [];
            this.filteredCompartimente = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isFilterLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (compartimente) => {
                this.compartimente = [...compartimente];
                this.filteredCompartimente = [...compartimente];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            }
        });
    }

    onCompartimenteSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.searchParamsForm.get('compartiment')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimente;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterCompartimente(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.compartimente.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        ));
    }

    resetForm(): void {
        this.activeValue = '-1';
        this.publicValue = '-1';

        this.rerenderDT();
        this.showCard = true;
        this.searchParamsForm.reset(this.initialFormValues);
        this.onDirectieChange(-1);

        this.filteredDirectii = this.directii;
        this.directiiControl.setValue(null);

        this.compartimenteControl.setValue(null);

        this.destroy$.next();
        this.destroy$.complete();
        this.renderFieldsSearch();

        if (this.showTreeView) {
            this.loadTreeView();
        }
    }

    applyFilters(): void {
        this.rerenderDT(true);

        if (this.showTreeView) {
            this.loadTreeView();
        }
    }

    toggleCard(): void {
        this.showCard = !this.showCard;
    }

    renderDT(): void {
        this.isLoading = true;
        this.dtOptions = {
            serverSide: true,
            responsive: true,
            searching: false,
            pageLength: 25,
            // scrollX: true,
            // columnDefs: [
            //     { targets: 0, width: '60px' },      // ID
            //     { targets: 1, width: '300px' },     // Categorie  
            //     { targets: 2, width: '200px' },     // Organizare
            //     { targets: 3, width: '150px' },     // Template-uri
            //     { targets: 4, width: '120px' },     // Status
            //     { targets: 5, width: '180px' }      // Acțiuni
            // ],
            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;
                dataTablesParameters.active = this.activeValue;
                dataTablesParameters.public_visible = this.publicValue;
                Object.assign(dataTablesParameters, { ...this.searchParamsForm.value });

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                    width: '60px'
                },
                {
                    title: 'Categorie',
                    data: 'name',
                    width: '300px',
                    render: (data: any, type: any, row: any) => {
                        let html = `<strong>${row.name}</strong>`;
                        if (row.description) {
                            html += `<br><small class="text-muted">${row.description}</small>`;
                        }
                        if (row.parent_category_id) {
                            html += `<br><small class="text-info"><i class="fas fa-link"></i> Subcategorie</small>`;
                        }
                        return html;
                    }
                },
                {
                    title: 'Organizare',
                    data: 'directie_name',
                    sortable: false,
                    render: (data: any, type: any, row: any) => {
                        return `
                                    <strong>Dir:</strong> ${row.directie_name}
                                    <br><strong>Comp:</strong> ${row.compartiment_name}
                                `
                    }
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (row: any) => {
                        return `
                            <div class="d-flex align-center justify-content-left">
                                <button class="action-btn edit me-1" title="Editează">
                                    <img class="btn-img" src="../../../../assets/icons/edit.svg" alt="edit"/>
                                </button>
                                <button class="action-btn delete me-1" title="Șterge">
                                    <img class="btn-img" src="../../../../assets/icons/delete.svg" alt="delete"/>
                                </button>
                                <button type="button" class="action-btn update_status_active me-1 ` +
                            (row.active == 1 ? 'action-success' : 'action-danger') +
                            `" title="Schimbare status activ">
									<img class="btn-img" src="` +
                            (row.active == 1
                                ? '/assets/icons/check-white.svg'
                                : '/assets/icons/close-white.svg') +
                            `" alt="update_status_active"/>
								</button>
								<button type="button" class="action-btn update_status_public me-1 ` +
                            (row.public_visible == true ? 'action-success' : 'action-danger') +
                            `" title="Schimbare status public">
									<img class="btn-img" src="` +
                            (row.public_visible == true
                                ? '/assets/icons/public_white.svg'
                                : '/assets/icons/public_not_white.svg') +
                            `" alt="update_status_public"/>
								</button>
                            </div>
                        `;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');

                const editIcon = lastTd?.querySelector('.edit') as HTMLElement;
                const deleteIcon = lastTd?.querySelector('.delete') as HTMLElement;
                const updateStatusActiveIcon = lastTd?.querySelector('.update_status_active') as HTMLElement;
                const updateVisibilityIcon = lastTd?.querySelector('.update_status_public') as HTMLElement;

                if (editIcon) {
                    if (!this.permissionsService.hasPermission('dgaspc-module.settings.template-categories.update')) {
                        editIcon.style.display = 'none';
                    }
                    editIcon.addEventListener('click', () => {
                        this.openEditDialog(data);
                    });
                }

                if (deleteIcon) {
                    if (!this.permissionsService.hasPermission('dgaspc-module.settings.template-categories.delete')) {
                        deleteIcon.style.display = 'none';
                    }
                    deleteIcon.addEventListener('click', () => {
                        this.delete(data.id);
                    });
                }

                if (updateStatusActiveIcon) {
                    if (!this.permissionsService.hasPermission('dgaspc-module.settings.template-categories.update')) {
                        updateStatusActiveIcon.style.display = 'none';
                    }
                    updateStatusActiveIcon.addEventListener('click', () => {
                        this.changeStatusActive(data.id);
                    });
                }

                if (updateVisibilityIcon) {
                    if (!this.permissionsService.hasPermission('dgaspc-module.settings.template-categories.update')) {
                        updateVisibilityIcon.style.display = 'none';
                    }
                    updateVisibilityIcon.addEventListener('click', () => {
                        this.changeStatusPublic(data.id);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(paginate: boolean = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });
        this.showCard = false;
    }

    openAddDialog(): void {
        const dialogRef = this.dialog.open(AddTemplateCategoryComponent, {
            autoFocus: false,
            data: this.directii
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                this.loadStatistics();
                if (this.showTreeView) {
                    this.loadTreeView();
                }
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Categoria a fost adăugată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openEditDialog(data: any): void {
        const dialogRef = this.dialog.open(EditTemplateCategoryComponent, {
            data: {
                data: data,
                directiiActive: this.directii,
            },
            autoFocus: false,
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                this.loadStatistics();
                if (this.showTreeView) {
                    this.loadTreeView();
                }
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Categoria a fost actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    delete(id: number): void {
        Swal.fire({
            title: 'Sunteți sigur?',
            text: 'Nu o să puteți recupera datele odată cu ștergerea lor!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Confirm!',
            cancelButtonText: 'Cancel',
            showLoaderOnConfirm: true,
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.templateCategoriesService.delete(id).subscribe({
                    next: () => {
                        this.rerenderDT();
                        this.loadStatistics();
                        if (this.showTreeView) {
                            this.loadTreeView();
                        }
                        Swal.fire({
                            title: 'Șters!',
                            text: 'Categoria a fost ștearsă cu succes.',
                            icon: 'success',
                            confirmButtonText: 'OK',
                        });
                    },
                    error: (err: any) => {
                        Swal.fire({
                            title: helper.dialogConfig.headers.errorAtentie,
                            text: err.error.errors.message[0] || 'A apărut o eroare la ștergere.',
                            icon: 'error',
                            confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        });
                    }
                });
            }
        });
    }

    changeStatusActive(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'O să actualizați statusul înregistrării selectate!',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.changeStatusActiveAction(id);
            }
        });
    }

    changeStatusActiveAction(id: number) {
        this.isLoading = true;
        console.log(id);
        this.templateCategoriesService.toggleStatus(id).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err: any) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.changeStatusActiveAction(id);
                    }
                });
            }
        );
    }

    changeStatusPublic(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'O să actualizați statusul înregistrării selectate!',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.changeStatusPublicAction(id);
            }
        });
    }

    changeStatusPublicAction(id: number) {
        this.isLoading = true;
        this.templateCategoriesService.toggleVisibility(id).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.changeStatusPublicAction(id);
                    }
                });
            }
        );
    }

    migrateExistingTemplates(): void {
        Swal.fire({
            title: 'Migrare Template-uri',
            text: 'Doriți să migrați template-urile existente în noua structură de categorii?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Da, migrează!',
            cancelButtonText: 'Anulează',
            showLoaderOnConfirm: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.templateCategoriesService.migrateTemplates().subscribe({
                    next: (response) => {
                        const results = response.data.results;
                        const stats = results.statistics;

                        Swal.fire({
                            title: 'Migrare Completă',
                            html: `
                                <p>Template-uri migrate: <strong>${stats.total_migrated}</strong></p>
                                <p>Template-uri oficiale: <strong>${stats.oficial_migrated}</strong></p> 
                                <p>Template-uri cereri: <strong>${stats.cerere_migrated}</strong></p>
                                <p>Erori: <strong>${stats.total_errors}</strong></p>
                            `,
                            icon: 'success',
                            confirmButtonText: 'OK'
                        });

                        this.rerenderDT();
                        this.loadStatistics();
                        if (this.showTreeView) {
                            this.loadTreeView();
                        }
                    },
                    error: (err: any) => {
                        Swal.fire({
                            title: 'Eroare la Migrare',
                            text: err.error.errors.message[0] || 'A apărut o eroare la migrare.',
                            icon: 'error',
                            confirmButtonText: 'OK'
                        });
                    }
                });
            }
        });
    }
}